import { FC, ReactNode, useState, Fragment } from 'react';
import Button, { ButtonProps } from '../Button';
import { Dialog, Transition } from '@headlessui/react';

export type AlertDialogWrapperProps = {
  /** The Title of Dialog */
  title: string;
  /** The Description of Dialog */
  description: string;
  /** The label of the cancel button
   * by default it's Cancel
   */
  cancelLabel?: string;
  /** The label of the confirm button
   * by default it's Confirm
   */
  confirmLabel?: string;
  /** function that gets executed when the user clicks the confirm button */
  onConfirm: () => void;
  /** Optional property that changes the color of the confirm button from primary to red */
  isDestructive?: boolean;
  /** for now this wrapper can only wrap buttons, this children prop will be the children prop for the wrapped button */
  thirdButton?: {
    label: string;
    onClick: () => void;
    className?: string;
  };
  children: ReactNode;
};

export function AlertDialogWrapper<
  P extends AlertDialogWrapperProps & ButtonProps
>(Component: FC<ButtonProps>): React.FC<P> {
  const Wrapped: FC<P> = ({
    thirdButton,
    children,
    title,
    description,
    cancelLabel = 'Cancel',
    confirmLabel = 'Confirm',
    isDestructive = false,
    onConfirm,
    ...props
  }) => {
    const [open, setOpen] = useState(false);

    return (
      <>
        <Transition.Root show={open} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={setOpen}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                          <Dialog.Title
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900"
                          >
                            {title}
                          </Dialog.Title>
                          <div className="mt-2">
                            <p
                              className="text-sm text-gray-500"
                              dangerouslySetInnerHTML={{ __html: description }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`bg-gray-50 px-4 py-3 ${
                        thirdButton ? 'justify-between' : ''
                      } sm:flex sm:flex-row-reverse sm:px-6`}
                    >
                      <div className="sm:flex flex-row-reverse">
                        <Button
                          color="custom"
                          type="button"
                          className={`inline-flex w-full justify-center rounded-md ${
                            isDestructive
                              ? 'bg-red-600 hover:bg-red-500'
                              : 'bg-primary-600 hover:bg-primary-500'
                          }  px-3 py-2 text-sm font-semibold text-white shadow-sm  sm:ml-3 sm:w-auto`}
                          onClick={() => {
                            onConfirm();
                            setOpen(false);
                          }}
                        >
                          {confirmLabel}
                        </Button>
                        {thirdButton && (
                          <Button
                            color="custom"
                            type="button"
                            className={`inline-flex w-full justify-center rounded-md ${
                              thirdButton.className
                                ? thirdButton.className
                                : 'bg-gray-800 hover:bg-gray-950'
                            }  px-3 py-2 text-sm font-semibold text-white shadow-sm mt-1 sm:mt-0 sm:ml-3 sm:w-auto`}
                            onClick={() => {
                              thirdButton.onClick();
                              setOpen(false);
                            }}
                          >
                            {thirdButton.label}
                          </Button>
                        )}
                      </div>
                      <Button
                        type="button"
                        color="custom"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        onClick={() => setOpen(false)}
                      >
                        {cancelLabel}
                      </Button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
        <Component {...props} type="button" onClick={() => setOpen(true)}>
          {children}
        </Component>
      </>
    );
  };

  return Wrapped;
}
